/**
 * Document recycle bin — /documents/recycle-bin. Deleted documents with who removed them and why;
 * a document administrator (or the owner) restores one back into its folder. The generic action
 * dialog cannot offer this: its record picker only lists documents that are not deleted.
 */
import { useState } from 'react';
import { api } from '../lib/api.ts';
import { invalidate, useQuery } from '../lib/hooks.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useMeta } from '../lib/meta.tsx';
import { Link, useRoute } from '../lib/router.tsx';
import { Button, Card, EmptyState, ErrorState, Spinner, StatusBadge, useToast } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { PageHead } from '../components/PageHead.tsx';
import type { Row } from '../lib/types.ts';

export function RecycleBinPage() {
  const { t, locale, date } = useI18n();
  const { page, entities } = useMeta();
  const { path } = useRoute();
  const toast = useToast();
  const nav = page(path);
  const def = entities.get('document');
  const bin = useQuery<Row[]>('documents:recycle-bin', () => api.get<Row[]>('queries/documents.recycle_bin'), { ttlMs: 0 });
  const [busy, setBusy] = useState<string | null>(null);
  const restore = async (id: string) => {
    setBusy(id);
    try {
      await api.post('actions/documents.restore', { documentId: id });
      invalidate('list:'); invalidate('rec:'); invalidate('dash:');
      toast.success(t('docs.restored'));
      await bin.refresh();
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };
  const statusOptions = def?.fields.find((f) => f.name === 'status')?.options ?? null;
  const rows = bin.data ?? [];
  return (
    <div className="stack" data-recycle-bin>
      <PageHead title={<><Icon name="Trash2" size={22} className="muted" />{t('docs.recycleBin')}</>} sub={t('docs.recycleBinIntro')} crumbs={nav ? [{ label: nav.module.label, to: nav.module.path }] : []}
        actions={<Button icon="RefreshCw" onClick={() => void bin.refresh()} busy={bin.loading}>{t('common.refresh')}</Button>} />
      <Card flush>
        {bin.error ? <ErrorState error={bin.error} retry={() => void bin.refresh()} /> : bin.loading && !bin.data ? <Spinner /> : rows.length === 0 ? <EmptyState title={t('docs.binEmpty')} hint={t('docs.binEmptyHint')} icon="Trash2" /> : (
          <div className="table-wrap">
            <table className="table" data-recycle-list>
              <thead><tr><th>{t('docs.number')}</th><th>{t('docs.title')}</th><th>{t('docs.version')}</th><th>{t('common.status')}</th><th>{t('docs.deletedAt')}</th><th>{t('docs.deletedBy')}</th><th>{t('common.reason')}</th><th /></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id)} data-doc-id={String(r.id)} data-doc-number={String(r.number)}>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>{String(r.number)}</td>
                    <td>{locale === 'ar' && r.title_ar ? String(r.title_ar) : String(r.title)}<div className="muted" style={{ fontSize: 12 }}>{[r.category, r.folder, r.file_name].filter(Boolean).map(String).join(' · ')}</div></td>
                    <td className="num">v{String(r.version_no)}</td>
                    <td><StatusBadge value={r.status} options={statusOptions as never} /></td>
                    <td>{date(r.deleted_at)}</td>
                    <td>{String(r.deleted_by_name ?? '—')}</td>
                    <td>{String(r.deleted_reason ?? '—')}</td>
                    <td className="row-actions"><Button size="sm" icon="RotateCcw" busy={busy === r.id} data-restore={String(r.id)} onClick={() => void restore(String(r.id))}>{t('docs.restore')}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {def && <p className="muted" style={{ fontSize: 12 }}>{t('docs.binHint')} <Link to={def.route} className="link">{def.labelPlural}</Link></p>}
    </div>
  );
}
